import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BasicInfoData } from '../../models/global';
import { ChangeLangService } from './change-lang';
import { SessionService } from './session';

/** The slice of Tawk's JavaScript API (developer.tawk.to/jsapi) this service uses. */
interface TawkApi {
  customStyle?: { zIndex: number };
  onBeforeLoad?: () => void;
  onLoad?: () => void;
  onStatusChange?: (status: TawkStatus) => void;
  onChatMaximized?: () => void;
  onChatMinimized?: () => void;
  onChatMessageAgent?: () => void;
  showWidget(): void;
  hideWidget(): void;
  maximize(): void;
  minimize(): void;
  isChatMaximized(): boolean;
  logout(callback: (error?: unknown) => void): void;
  setAttributes(attributes: Record<string, string>, callback: (error?: unknown) => void): void;
}

export type TawkStatus = 'online' | 'away' | 'offline';

/**
 * Which Tawtheiq user the browser's Tawk visitor was identified as. Deliberately not `twk_`-prefixed:
 * clearTawkStorage() removes every key that is.
 */
const OWNER_KEY = 'tawkVisitorOwner';

/**
 * Tawk.to live support, alongside Ask Tawtheiq.
 *
 * - Signed out (login, sign-up…), Tawk's own bubble is the only way in.
 * - Signed in, the bubble is hidden and the Ask Tawtheiq launcher offers "Ask Tawtheiq" or
 *   "Contact support" (openChat()); minimising the chat hides it again. The visitor is named after
 *   the user (setAttributes), so agents see who is asking.
 * - Signing out wipes the visitor — Tawk's cookies and storage cleared, then Tawk's own logout()
 *   refreshes the session in place — so the next person at this browser starts with an
 *   empty chat. A sign-out the page never saw (tab closed) is caught on the next load, before the
 *   widget reads anything (OWNER_KEY).
 * - Each language has its own Tawk widget (environment.tawkTo.widgetId) — Tawk translates and
 *   mirrors a widget by its Language setting — picked when the page loads. A language change
 *   applies on the next load: Tawk's switchWidget() would swap it live, but afterwards hideWidget()
 *   no longer hides anything, which the signed-in app depends on.
 *
 * All of the above was checked against the real widget in a browser, including: hiding in
 * onBeforeLoad means the bubble never paints; the wipe only yields a new visitor once the cookies
 * are also cleared on the parent domain (Tawk sets them on e.g. `.tawtheiq.com`); logout() needs
 * the session token, so the wipe keeps that one key; hideWidget() does not hide a maximised chat.
 */
@Injectable({ providedIn: 'root' })
export class TawkChatService {
  private readonly session = inject(SessionService);
  private readonly lang = inject(ChangeLangService);
  private readonly config = environment.tawkTo;

  /** The widget is up; until then (or in a build without it) there is no live support to offer. */
  readonly ready = signal(false);
  /** The chat is open over the app, opened from the launcher. */
  readonly open = signal(false);
  /** Agent messages that arrived while the chat was closed. */
  readonly unread = signal(0);
  readonly status = signal<TawkStatus>('offline');

  private wiping = false;
  private identifiedAs: string | null = null;

  constructor() {
    if (!this.config.propertyId) return;
    this.load();
    effect(() => {
      const user = this.session.currentUser();
      const company = this.session.activeEnterprise()?.name ?? '';
      if (this.ready()) untracked(() => this.syncUser(user, company));
    });
  }

  /** "Contact support": show the chat over the app. */
  openChat(): void {
    if (!this.ready()) return;
    this.open.set(true);
    this.unread.set(0);
    this.api.showWidget();
    this.api.maximize();
  }

  /** Put the chat away (e.g. when Ask Tawtheiq opens instead). onChatMinimized hides it. */
  closeChat(): void {
    if (this.ready() && this.open()) this.api.minimize();
  }

  private get api(): TawkApi {
    return (window as any).Tawk_API;
  }

  private load(): void {
    const owner = localStorage.getItem(OWNER_KEY);
    if (owner && owner !== this.session.currentUser()?.userId) {
      clearTawkStorage();
      localStorage.removeItem(OWNER_KEY);
    }

    const w = window as any;
    const api: Partial<TawkApi> = (w.Tawk_API = w.Tawk_API || {});
    w.Tawk_LoadStart = new Date();
    // Tawk's default z-index (2,000,000,000) paints the chat over PrimeNG dialogs, drawers and
    // toasts; 1000 matches the launcher, under every overlay (1100+). Read once, at download.
    api.customStyle = { zIndex: 1000 };
    // Before the first paint, so a signed-in user never sees the bubble flash on page load.
    api.onBeforeLoad = () => this.applyVisibility();
    api.onLoad = () => {
      this.applyVisibility();
      this.ready.set(true);
    };
    api.onStatusChange = (status) => this.status.set(status);
    api.onChatMaximized = () => this.unread.set(0);
    api.onChatMinimized = () => {
      this.open.set(false);
      this.applyVisibility();
    };
    api.onChatMessageAgent = () => {
      if (!this.api.isChatMaximized()) this.unread.update((n) => n + 1);
    };

    const widgetId = this.lang.currentLang() === 'ar' ? this.config.widgetId.ar : this.config.widgetId.en;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${this.config.propertyId}/${widgetId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    document.head.appendChild(script);
  }

  private syncUser(user: BasicInfoData | undefined, company: string): void {
    if (this.wiping) return; // the wipe's callback runs this again
    const owner = localStorage.getItem(OWNER_KEY);
    if (owner && owner !== user?.userId) {
      this.wipe();
      return;
    }
    this.applyVisibility();
    if (!user) return;

    localStorage.setItem(OWNER_KEY, user.userId);
    const name = user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ');
    const attributes: Record<string, string> = {};
    for (const [key, value] of Object.entries({ name, email: user.email, company })) {
      if (value) attributes[key] = value.slice(0, 255); // Tawk's per-value limit
    }
    const signature = JSON.stringify(attributes);
    if (signature === this.identifiedAs) return;
    this.identifiedAs = signature;
    // name/email are accepted without a hash while the property's Secure Mode is off.
    this.setAttributes(attributes);
  }

  /** Tawk refuses the whole call over an email it won't take (INVALID_EMAIL); keep the rest. */
  private setAttributes(attributes: Record<string, string>): void {
    this.api.setAttributes(attributes, (error) => {
      if (!error) return;
      if (error === 'INVALID_EMAIL' && attributes['email']) {
        const withoutEmail = { ...attributes };
        delete withoutEmail['email'];
        this.setAttributes(withoutEmail);
        return;
      }
      this.identifiedAs = null;
      console.warn('[Tawk] could not identify the visitor', error);
    });
  }

  /** Sign-out: nothing of this user's chat may survive in the browser. */
  private wipe(): void {
    this.wiping = true;
    localStorage.removeItem(OWNER_KEY);
    this.identifiedAs = null;
    this.open.set(false);
    this.unread.set(0);
    // No endChat(): it pops the widget open on a "chat ended" screen, even while hidden.
    // logout() authenticates with the visitor's session token, so that one key stays; the visitor
    // id goes, which is what makes the refreshed session a new visitor.
    const tokenKey = `twk_token_${this.config.propertyId}`;
    const token = localStorage.getItem(tokenKey);
    clearTawkStorage();
    if (token) localStorage.setItem(tokenKey, token);

    // logout() reports a refused token, but returns without calling back when its own token check
    // throws, hence the timeout.
    const done = (error?: unknown) => {
      if (!this.wiping) return;
      clearTimeout(timeout);
      this.wiping = false;
      if (error) {
        // The old visitor may still be on screen: no support until the next page load, which
        // starts clean (storage is already cleared).
        console.warn('[Tawk] could not reset the visitor', error);
        this.api.hideWidget();
        this.ready.set(false);
        return;
      }
      // Catch up on anything that changed meanwhile (e.g. the next user already signed in).
      this.syncUser(this.session.currentUser(), this.session.activeEnterprise()?.name ?? '');
    };
    const timeout = setTimeout(() => done('timeout'), 10_000);
    this.api.logout(done);
  }

  private applyVisibility(): void {
    if (this.session.currentUser() && !this.open()) {
      // hideWidget() leaves an open chat window on screen (e.g. one opened on the login page).
      if (this.api.isChatMaximized()) this.api.minimize();
      this.api.hideWidget();
    } else {
      this.api.showWidget();
    }
  }
}

/**
 * Every trace of the Tawk visitor in this browser. Its cookies live on the registrable domain
 * (`.tawtheiq.com`, not the host), so the delete is tried at each parent level — a host-only
 * delete leaves the visitor id in place.
 */
export function clearTawkStorage(): void {
  const isTawk = (key: string) => /^(twk_|Tawk)/.test(key);
  const labels = location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < labels.length - 1; i++) domains.push(`; domain=.${labels.slice(i).join('.')}`);

  const cookies = document.cookie.split('; ').map((cookie) => cookie.split('=')[0]);
  for (const name of cookies.filter(isTawk)) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
  for (const store of [localStorage, sessionStorage]) {
    Object.keys(store).filter(isTawk).forEach((key) => store.removeItem(key));
  }
}
